import React, { useMemo } from 'react';
import {
  IconChart,
  IconDashboard,
  IconList,
  IconShield,
  IconUsers,
} from './Icons';
import './MobileBottomNav.css';

/**
 * Twitter/X-style fixed bottom navigation for mobile. Five primary destinations:
 * Overview, Results, My Portfolio (center), Portfolio Sim, and Risk Tools hub.
 */
function MobileBottomNav({
  currentPage,
  riskMode,
  onNavigate,
  onModeChange,
  isPro = false,
  showMyPortfolio = true,
  showMyPortfolioProBadge = false,
  // Users → Feature access switches (hide only).
  canSeeFeature = () => true,
}) {
  const riskToolsActive =
    currentPage === 'risk-tools'
    || currentPage === 'hedging-risk'
    || currentPage === 'prop-firm-planner'
    || currentPage === 'trade-watcher'
    || ['position-sizer', 'concentration-risk', 'drawdown-risk', 'stop-planner', 'rebalance'].includes(currentPage)
    || (currentPage === 'home' && (riskMode === 'risk' || riskMode === 'apexMae'));

  const items = useMemo(() => {
    const nav = [
      {
        key: 'dashboard',
        label: 'Overview',
        Icon: IconDashboard,
        active: currentPage === 'dashboard',
        onClick: () => onNavigate('dashboard'),
        showProBadge: false,
      },
      {
        key: 'results',
        label: 'Results',
        Icon: IconChart,
        active: currentPage === 'results',
        onClick: () => onNavigate('results'),
        showProBadge: false,
      },
    ];

    if (showMyPortfolio) {
      nav.push({
        key: 'client-reporting',
        label: 'My Portfolio',
        Icon: IconUsers,
        active: currentPage === 'client-reporting',
        onClick: () => onNavigate('client-reporting'),
        showProBadge: showMyPortfolioProBadge,
        isCenter: true,
      });
    }

    nav.push(
      {
        key: 'portfolio-simulator',
        label: 'Portfolio Sim',
        Icon: IconList,
        active: currentPage === 'portfolio-simulator',
        onClick: () => onNavigate('portfolio-simulator'),
        showProBadge: !isPro,
      },
      {
        key: 'risk-tools',
        label: 'Risk Tools',
        Icon: IconShield,
        active: riskToolsActive,
        onClick: () => onNavigate('risk-tools'),
        showProBadge: !isPro,
      },
    );

    // Hide switched-off destinations. Risk Tools always stays: the portfolio
    // tools in it can't be switched off.
    const featureFor = {
      dashboard: 'overview',
      results: 'brand_results',
      'client-reporting': 'my_portfolio',
      'portfolio-simulator': 'portfolio_simulator',
    };
    return nav.filter((item) => !featureFor[item.key] || canSeeFeature(featureFor[item.key]));
  }, [
    canSeeFeature,
    currentPage,
    isPro,
    onNavigate,
    riskToolsActive,
    showMyPortfolio,
    showMyPortfolioProBadge,
  ]);

  return (
    <nav className="mobile-bottom-nav" role="navigation" aria-label="Primary">
      {items.map(({ key, label, Icon, active, onClick, showProBadge, showBetaBadge, isCenter }) => (
        <button
          key={key}
          type="button"
          className={`mobile-bottom-nav-btn${active ? ' is-active' : ''}${isCenter ? ' is-center' : ''}`}
          onClick={onClick}
          aria-label={
            showProBadge ? `${label} (Pro)` : showBetaBadge ? `${label} (Beta)` : label
          }
          aria-current={active ? 'page' : undefined}
        >
          <span className="mobile-bottom-nav-icon-wrap" aria-hidden="true">
            <span className="mobile-bottom-nav-icon">
              <Icon size={22} />
            </span>
            {showBetaBadge ? <span className="mobile-bottom-nav-beta-dot">BETA</span> : null}
            {showProBadge ? <span className="mobile-bottom-nav-pro-dot">PRO</span> : null}
          </span>
          <span className="mobile-bottom-nav-label">{label}</span>
        </button>
      ))}
    </nav>
  );
}

export default MobileBottomNav;
